import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Dropdown, Menu } from '@arco-design/web-react';
import { IconDown } from '@arco-design/web-react/icon';
import { exportDatasets } from '@dmp/api/api';
import ApiTokenIcon from '@dmp/assets/icons/api-token.svg?react';
import ExportFileIcon from '@dmp/assets/icons/export-file.svg?react';
import InternalAppIcon from '@dmp/assets/icons/internal-app.svg?react';
import LogoutIcon from '@dmp/assets/icons/logout.svg?react';
import ProfileIcon from '@dmp/assets/icons/profile.svg?react';
import SettingsIcon from '@dmp/assets/icons/settings.svg?react';
import { ModalService } from '@dmp/components/ui/ModalService/ModalService';
import { ROUTE_PATHS, TRACK_EVENT_TYPES } from '@dmp/constant';
import { useGlobalStore } from '@dmp/stores';
import { getAvatarInitial } from '@dmp/utils';
import { logout } from '@dmp/utils/auth';
import { downloadFileFromBlob } from '@dmp/utils/file';
import { isCentificUser, isRbacAdminFromToken, isRbacEnabled, isUploaderFromToken } from '@dmp/utils/rbac';
import { trackEvent } from '@dmp/utils/track';
import dayjs from 'dayjs';

import './UserDropdown.css';
import { UserDropdownKey } from './UserDropdown.types';

export const UserDropdown = () => {
  const userInfo = useGlobalStore((s) => s.userInfo);
  const navigate = useNavigate();
  const isSsoUser = useGlobalStore((s) => s.isSsoUser);
  const location = useLocation();
  const internalAppsPermission = useGlobalStore((s) => s.internalAppsPermission);

  const haveAccessToInternalApps = useMemo(() => {
    // JWT grants an internal app, OR the user is a DMP Uploader or RBAC Administrator — both
    // read straight from the JWT's top-level claims (`is_uploader`/`is_rbac_admin`, embedded at
    // token-mint time) — synchronous, no separate GET /api/v1/admin/me/ call. Uploaders/RBAC
    // Administrators have no internal-app claim in the JWT but still reach the Internal Apps
    // portal (RBAC Administrator sees only the RBAC tile; Uploader sees Dataset Management only).
    const hasJwtApp = Object.values(internalAppsPermission).some((permission) => permission);
    // isRbacEnabled() guards against a narrow edge case: a still-valid token minted while RBAC was
    // enabled, carrying a now-stale is_rbac_admin=true claim, after an ops-level toggle turned
    // ENTRA_AUTH_ENABLED back off for this tenant.
    const hasDmpRole = isUploaderFromToken() || (isRbacEnabled() && isRbacAdminFromToken());
    return (hasJwtApp || hasDmpRole) && isCentificUser(userInfo?.user_email);
  }, [internalAppsPermission, userInfo?.user_email]);

  const isDatasetManagementAdmin = !!internalAppsPermission.dataset_management;
  // API Tokens are for Dataset Management programmatic submission — available to its admins and
  // Uploaders. RBAC Administrator has no Dataset Management access, so it's excluded here.
  const showApiTokens = isDatasetManagementAdmin || isUploaderFromToken();

  const selectedKeys = useMemo(() => {
    if (location.pathname === ROUTE_PATHS.PROFILE) {
      return [UserDropdownKey.Profile];
    } else if (location.pathname === ROUTE_PATHS.INTERNAL_APP_PORTAL) {
      return [UserDropdownKey.InternalApps];
    } else if (location.pathname === ROUTE_PATHS.SETTINGS) {
      return [UserDropdownKey.Settings];
    }
    return [];
  }, [location.pathname]);

  const downloadExportDatasets = async () => {
    try {
      const res = await exportDatasets();
      const blob = new Blob([res.data], {
        type:
          (res.headers?.['content-type'] as string) ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const fileName = `CentificDataCatalog${dayjs().format('DDMMYYYY')}.XLSX`;
      downloadFileFromBlob({
        blob,
        fileName,
      });
    } catch (err) {
      ModalService.error({
        title: 'Failed to export datasets',
        content: err instanceof Error ? err.message : 'An unknown error occurred',
      });
    }
  };

  const dropList = (
    <Menu style={{ width: 180 }} selectedKeys={selectedKeys} className="user-dropdown-menu">
      <Menu.Item key={UserDropdownKey.Profile} onClick={() => navigate(ROUTE_PATHS.PROFILE)}>
        <ProfileIcon />
        <span className="text-[#0038FF]">Profile</span>
      </Menu.Item>

      {isCentificUser(userInfo?.user_email) && (
        <Menu.Item key={UserDropdownKey.Export} onClick={downloadExportDatasets}>
          <ExportFileIcon />
          <span className="text-[#0038FF]"> Export Catalog</span>
        </Menu.Item>
      )}

      {haveAccessToInternalApps && (
        <Menu.Item key={UserDropdownKey.InternalApps} onClick={() => navigate(ROUTE_PATHS.INTERNAL_APP_PORTAL)}>
          <InternalAppIcon />
          <span className="text-[#0038FF]">Internal Apps</span>
        </Menu.Item>
      )}

      <Menu.Item key={UserDropdownKey.Settings} onClick={() => navigate(ROUTE_PATHS.SETTINGS)} disabled={isSsoUser}>
        <SettingsIcon fill={isSsoUser ? '#ccc' : '#5929D0'} />
        <span className={isSsoUser ? 'text-[#ccc]' : 'text-[#0038FF]'}>Settings</span>
      </Menu.Item>

      {showApiTokens && (
        <Menu.Item key={UserDropdownKey.ApiTokens} onClick={() => navigate(ROUTE_PATHS.API_TOKENS)}>
          <ApiTokenIcon />
          <span className="text-[#0038FF]">API Tokens</span>
        </Menu.Item>
      )}

      <Menu.Item
        key={UserDropdownKey.Logout}
        onClick={() => {
          ModalService.confirm({
            title: 'Are you sure you want to logout?',
            onOk: () => {
              // avoid browser to abort the xhr request after set the window.location.href, so we need to redirect after the request is done
              trackEvent(TRACK_EVENT_TYPES.LOGOUT).finally(() => {
                logout();
              });
            },
          });
        }}
      >
        <LogoutIcon className="w-[20px] h-[20px] " />
        <span className="text-[#CD128A]">Logout</span>
      </Menu.Item>
    </Menu>
  );

  return (
    <Dropdown droplist={dropList} trigger="click" triggerProps={{ className: 'dropdown-popup' }} position="tr">
      <div className="flex items-center gap-2 cursor-pointer">
        <div className="w-[42px] h-[42px] rounded-full bg-[#EFEAFB] text-[#858585] flex items-center justify-center">
          {getAvatarInitial(userInfo?.first_name ?? '', userInfo?.last_name ?? '')}
        </div>
        <div
          className="max-w-[160px] truncate font-semibold text-[#333]"
          title={`${userInfo?.first_name ?? ''} ${userInfo?.last_name ?? ''}`.trim()}
        >
          {userInfo?.first_name ?? ''} {userInfo?.last_name ?? ''}
        </div>
        <IconDown className="text-[#858585]!" />
      </div>
    </Dropdown>
  );
};
